import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, Sparkles, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import { ImageUploadInput } from "@/components/ui/image-upload-input";
import { VisibilityEditor } from "./visibility-editor";
import { SourceBadge } from "./source-badge";
import { StepPreviewIframe } from "./step-preview-iframe";
import { isStepReachable } from "./audience-reach";
import type {
  TourAudienceCriteria,
  TourStepRecord,
  TourStepPlacement,
} from "./types";
import { PLACEMENT_OPTIONS } from "./types";
import { DATA_TOUR_IDS } from "@/components/tours/data-tour-ids.generated";

interface StepDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tourId: string;
  tourRoutePattern: string;
  audienceCriteria: TourAudienceCriteria;
  step: TourStepRecord | null;
}

type TargetMode = "anchor" | "selector";
type StepType = "highlight" | "chapter";

function isValidSelector(selector: string): boolean {
  if (!selector.trim()) return false;
  try {
    document.querySelector(selector);
    return true;
  } catch {
    return false;
  }
}

export function StepDialog({ open, onOpenChange, tourId, tourRoutePattern, audienceCriteria, step }: StepDialogProps) {
  const { toast } = useToast();
  const isEdit = !!step;
  // P23C: step type — "highlight" (DOM-anchored) or "chapter" (centered modal card).
  const [stepType, setStepType] = useState<StepType>("highlight");
  const [targetMode, setTargetMode] = useState<TargetMode>("anchor");
  const [targetId, setTargetId] = useState("");
  const [targetSelector, setTargetSelector] = useState("");
  const [placement, setPlacement] = useState<TourStepPlacement>("auto");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [mediaFileId, setMediaFileId] = useState<string | null>(null);
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [visibility, setVisibility] = useState<TourAudienceCriteria>({});
  const [skipBeacon, setSkipBeacon] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  // P23a: pre-action sub-form. Open by default on edit when a
  // preAction already exists, so authors don't have to hunt for it.
  const [preActionEnabled, setPreActionEnabled] = useState(false);
  const [preActionExpanded, setPreActionExpanded] = useState(false);
  const [preActionSelector, setPreActionSelector] = useState("");
  const [preActionSettleMs, setPreActionSettleMs] = useState(150);

  useEffect(() => {
    if (open) {
      const initialStepType: StepType = step?.stepType ?? "highlight";
      setStepType(initialStepType);
      // Detect mode from existing record. New steps default to anchor
      // mode (the well-trodden path that requires a developer-wired
      // data-tour attribute).
      const initialMode: TargetMode = step?.targetSelector ? "selector" : "anchor";
      setTargetMode(initialMode);
      setTargetId(step?.targetId ?? "");
      setTargetSelector(step?.targetSelector ?? "");
      setPlacement(step?.placement ?? "auto");
      setTitle(step?.title ?? "");
      setBody(step?.body ?? "");
      setMediaFileId(step?.mediaFileId ?? null);
      setMediaUrl(step?.mediaUrl ?? null);
      setVisibility(step?.visibilityCriteria ?? {});
      setSkipBeacon(step?.skipBeacon ?? false);
      const hasPreAction = !!step?.preAction;
      setPreActionEnabled(hasPreAction);
      setPreActionExpanded(hasPreAction);
      setPreActionSelector(step?.preAction?.selector ?? "");
      setPreActionSettleMs(step?.preAction?.settleMs ?? 150);
    }
  }, [open, step]);

  const isChapter = stepType === "chapter";
  const trimmedTargetId = targetId.trim();
  const trimmedTargetSelector = targetSelector.trim();
  const trimmedPreActionSelector = preActionSelector.trim();
  const selectorValid = trimmedTargetSelector ? isValidSelector(trimmedTargetSelector) : true;
  const preActionSelectorValid = trimmedPreActionSelector
    ? isValidSelector(trimmedPreActionSelector)
    : true;

  const mutation = useMutation({
    mutationFn: async () => {
      // Chapter steps: both targets null, preAction null.
      // Highlight steps: XOR enforced by server Zod + DB CHECK.
      const targetIdToSend = isChapter ? null : (targetMode === "anchor" ? trimmedTargetId : null);
      const targetSelectorToSend = isChapter ? null : (targetMode === "selector" ? trimmedTargetSelector : null);
      const preActionToSend =
        !isChapter && preActionEnabled && trimmedPreActionSelector
          ? {
              kind: "click" as const,
              selector: trimmedPreActionSelector,
              settleMs: preActionSettleMs,
            }
          : null;
      const body_ = {
        stepType,
        targetId: targetIdToSend,
        targetSelector: targetSelectorToSend,
        preAction: preActionToSend,
        placement: isChapter ? "center" : placement,
        title: title.trim() || null,
        body,
        mediaFileId,
        visibilityCriteria: visibility,
        skipBeacon,
      };
      if (isEdit && step) {
        return apiRequest("PATCH", `/api/admin/tour-steps/${step.id}`, body_);
      }
      return apiRequest("POST", `/api/admin/tours/${tourId}/steps`, body_);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/tours", tourId, "steps"] });
      toast({ title: isEdit ? "Step updated" : "Step created" });
      onOpenChange(false);
    },
    onError: (err: Error) => {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    },
  });

  async function handleFileSelect(file: File) {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const csrfToken = document.cookie
        .split("; ")
        .find((c) => c.startsWith("XSRF-TOKEN="))
        ?.split("=")[1];
      const res = await fetch("/api/files", {
        method: "POST",
        body: formData,
        credentials: "include",
        headers: csrfToken ? { "X-XSRF-TOKEN": csrfToken } : {},
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Upload failed" }));
        throw new Error(err.error || "Upload failed");
      }
      const data = await res.json();
      const fileId = data.file?.id ?? data.id;
      const previewUrl = data.file?.thumbnailUrl ?? URL.createObjectURL(file);
      setMediaFileId(fileId);
      setMediaUrl(previewUrl);
      toast({ title: "Image uploaded" });
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  }

  function handleRemoveMedia() {
    setMediaFileId(null);
    setMediaUrl(null);
  }

  // Chapter steps only need body. Highlight steps need a target.
  const targetSatisfied = isChapter
    ? true
    : (targetMode === "anchor"
        ? trimmedTargetId.length > 0
        : trimmedTargetSelector.length > 0 && selectorValid);
  const preActionSatisfied =
    isChapter || !preActionEnabled || (trimmedPreActionSelector.length > 0 && preActionSelectorValid);
  const canSubmit =
    targetSatisfied && body.length > 0 && preActionSatisfied && !mutation.isPending;
  const isAi = step?.source === "ai_generated";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[95vw] max-h-[90vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? "Edit step" : "New step"}
            {isEdit && step && <SourceBadge source={step.source} />}
          </DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-[440px_1fr] gap-0 flex-1 min-h-0 overflow-hidden">
          <div className="overflow-y-auto px-6 pb-2 space-y-4">
        {isAi && (
          <div className="flex items-start gap-2 rounded-md border bg-muted/40 p-3 text-sm">
            <Sparkles className="w-4 h-4 mt-0.5 text-muted-foreground" />
            <p className="text-muted-foreground">
              Manual edits will override generated content.
            </p>
          </div>
        )}
        <div className="space-y-4">
          {/* P23C: Step type toggle — must be first so collapsing below is predictable */}
          <div className="space-y-2">
            <Label>Step type</Label>
            <Tabs value={stepType} onValueChange={(v) => setStepType(v as StepType)}>
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="highlight" data-testid="tab-step-type-highlight">
                  Highlight
                </TabsTrigger>
                <TabsTrigger value="chapter" data-testid="tab-step-type-chapter">
                  Chapter
                </TabsTrigger>
              </TabsList>
              <TabsContent value="chapter" className="pt-2">
                <p className="text-xs text-muted-foreground">
                  Chapter steps appear as a centered fullscreen card with no spotlight. Use them to introduce sections or provide context before highlighting elements.
                </p>
              </TabsContent>
            </Tabs>
          </div>

          {/* Target + Placement + Pre-action — hidden for chapter steps */}
          {!isChapter && (
            <>
              <div className="space-y-2">
                <Label>Target element</Label>
                <Tabs value={targetMode} onValueChange={(v) => setTargetMode(v as TargetMode)}>
                  <TabsList className="grid grid-cols-2 w-full">
                    <TabsTrigger value="anchor" data-testid="tab-target-anchor">
                      Anchor (data-tour)
                    </TabsTrigger>
                    <TabsTrigger value="selector" data-testid="tab-target-selector">
                      CSS selector
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="anchor" className="space-y-2 pt-3">
                    <Input
                      id="step-target"
                      value={targetId}
                      onChange={(e) => setTargetId(e.target.value)}
                      placeholder="data-tour ID"
                      data-testid="input-step-target"
                    />
                    {trimmedTargetId.length > 0 && (
                      (DATA_TOUR_IDS as readonly string[]).includes(trimmedTargetId) ? (
                        <p
                          className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1"
                          data-testid="text-target-status-ok"
                        >
                          <CheckCircle2 className="w-3 h-3" />
                          Wired up — anchor exists in the app.
                        </p>
                      ) : (
                        <p
                          className="text-xs text-amber-600 dark:text-amber-400 flex items-start gap-1"
                          data-testid="text-target-status-unknown"
                        >
                          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                          <span>
                            Not wired up yet — no <code className="rounded bg-muted px-1">data-tour="{trimmedTargetId}"</code> exists in the app, so this step will be silently skipped at runtime. A developer needs to add the anchor first.
                          </span>
                        </p>
                      )
                    )}
                    <div className="space-y-1 pt-1">
                      <p className="text-xs text-muted-foreground">
                        Pick a registered anchor:
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {DATA_TOUR_IDS.map((id) => {
                          const active = trimmedTargetId === id;
                          return (
                            <button
                              key={id}
                              type="button"
                              onClick={() => setTargetId(id)}
                              data-testid={`chip-target-${id}`}
                            >
                              <Badge
                                variant={active ? "default" : "outline"}
                                className="cursor-pointer font-mono text-[11px]"
                              >
                                {id}
                              </Badge>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </TabsContent>
                  <TabsContent value="selector" className="space-y-2 pt-3">
                    <Input
                      id="step-target-selector"
                      value={targetSelector}
                      onChange={(e) => setTargetSelector(e.target.value)}
                      placeholder='e.g. button[aria-label="Settings"]'
                      className="font-mono text-xs"
                      data-testid="input-step-target-selector"
                    />
                    {trimmedTargetSelector.length > 0 && !selectorValid && (
                      <p
                        className="text-xs text-destructive flex items-start gap-1"
                        data-testid="text-target-selector-invalid"
                      >
                        <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                        <span>
                          Invalid CSS selector — the browser cannot parse it.
                        </span>
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Use a CSS selector when the target has no{" "}
                      <code className="rounded bg-muted px-1">data-tour</code>{" "}
                      attribute. Prefer stable hooks like{" "}
                      <code className="rounded bg-muted px-1">aria-label</code>{" "}
                      or{" "}
                      <code className="rounded bg-muted px-1">data-testid</code>{" "}
                      over class names.
                    </p>
                    <p
                      className="text-xs text-amber-700 dark:text-amber-300 flex items-start gap-1"
                      data-testid="text-selector-fragility-warning"
                    >
                      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                      <span>
                        Manual selectors break when the underlying markup
                        changes — prefer <code className="rounded bg-muted px-1">data-tour</code> anchors when possible.
                      </span>
                    </p>
                  </TabsContent>
                </Tabs>
              </div>
              <div className="space-y-2">
                <Label htmlFor="step-placement">Placement</Label>
                <Select value={placement} onValueChange={(v) => setPlacement(v as TourStepPlacement)}>
                  <SelectTrigger id="step-placement" data-testid="select-step-placement">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLACEMENT_OPTIONS.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="step-title">Title {isChapter ? "" : "(optional)"}</Label>
            <Input
              id="step-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="input-step-title"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="step-body">Body (markdown)</Label>
            <Textarea
              id="step-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              data-testid="input-step-body"
            />
          </div>
          <div className="space-y-2">
            <Label>Image</Label>
            <ImageUploadInput
              previewUrl={mediaUrl}
              isUploading={isUploading}
              onFileSelect={handleFileSelect}
              onRemove={handleRemoveMedia}
              testIdPrefix="step-image"
            />
          </div>

          {!isChapter && (
            <>
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="step-skip-beacon">Skip beacon (auto-show)</Label>
                <Switch
                  id="step-skip-beacon"
                  checked={skipBeacon}
                  onCheckedChange={setSkipBeacon}
                  data-testid="switch-step-skip-beacon"
                />
              </div>
              <div className="border-t pt-4 space-y-3">
                <button
                  type="button"
                  className="flex items-center gap-1 text-sm font-semibold w-full text-left"
                  onClick={() => setPreActionExpanded((v) => !v)}
                  data-testid="button-toggle-preaction"
                >
                  {preActionExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                  Pre-action (auto-click before highlight)
                  {preActionEnabled && (
                    <Badge variant="secondary" className="ml-auto text-[10px]">
                      on
                    </Badge>
                  )}
                </button>
                {preActionExpanded && (
                  <div className="space-y-3 pl-5">
                    <div className="flex items-center justify-between gap-2">
                      <Label htmlFor="step-preaction-enabled" className="text-xs">
                        Click an element first
                      </Label>
                      <Switch
                        id="step-preaction-enabled"
                        checked={preActionEnabled}
                        onCheckedChange={setPreActionEnabled}
                        data-testid="switch-preaction-enabled"
                      />
                    </div>
                    {preActionEnabled && (
                      <>
                        <div className="space-y-1.5">
                          <Label htmlFor="step-preaction-selector" className="text-xs">
                            CSS selector to click
                          </Label>
                          <Input
                            id="step-preaction-selector"
                            value={preActionSelector}
                            onChange={(e) => setPreActionSelector(e.target.value)}
                            placeholder='e.g. button[aria-label="Open user menu"]'
                            className="font-mono text-xs"
                            data-testid="input-preaction-selector"
                          />
                          {trimmedPreActionSelector.length > 0 && !preActionSelectorValid && (
                            <p
                              className="text-xs text-destructive flex items-start gap-1"
                              data-testid="text-preaction-selector-invalid"
                            >
                              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                              <span>Invalid CSS selector.</span>
                            </p>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="step-preaction-settle" className="text-xs">
                            Settle delay (ms): {preActionSettleMs}
                          </Label>
                          <Input
                            id="step-preaction-settle"
                            type="number"
                            min={0}
                            max={2000}
                            step={50}
                            value={preActionSettleMs}
                            onChange={(e) => {
                              const n = parseInt(e.target.value, 10);
                              if (Number.isFinite(n)) {
                                setPreActionSettleMs(Math.max(0, Math.min(2000, n)));
                              }
                            }}
                            data-testid="input-preaction-settle"
                          />
                          <p className="text-xs text-muted-foreground">
                            Wait this long after clicking before highlighting
                            the target. Use this to let menus/animations
                            finish (0–2000ms).
                          </p>
                        </div>
                        <div className="rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 p-2.5 text-xs text-amber-900 dark:text-amber-100">
                          <strong>Heads up:</strong> the click runs every time
                          this step is shown (including jumps from the table
                          of contents). Pick a control that{" "}
                          <em>opens or expands</em> rather than{" "}
                          <em>toggles</em> — otherwise revisiting the step
                          will close what it just opened.
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold mb-2">Visibility</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Restrict who sees this step. Empty fields = inherit audience.
            </p>
            <VisibilityEditor value={visibility} onChange={setVisibility} />
            {(() => {
              const reach = isStepReachable(visibility, audienceCriteria);
              if (reach.reachable) return null;
              return (
                <div
                  className="mt-3 rounded-md border border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40 p-2.5 text-xs text-amber-900 dark:text-amber-100 flex items-start gap-2"
                  data-testid="text-step-unreachable-warning"
                >
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{reach.reason}</span>
                </div>
              );
            })()}
          </div>
        </div>
          </div>
          <div
            className="hidden md:flex flex-col border-l bg-muted/10 min-h-[400px]"
            data-testid="container-step-preview"
          >
            {isChapter ? (
              <div className="flex-1 flex items-center justify-center p-8 text-center">
                <div className="space-y-2 max-w-xs">
                  <p className="text-sm font-semibold text-foreground">{title || "Chapter title"}</p>
                  <p className="text-xs text-muted-foreground">{body || "Chapter body text will appear here."}</p>
                  {mediaUrl && (
                    <img src={mediaUrl} alt="" className="mt-2 max-h-32 w-auto rounded-md mx-auto" />
                  )}
                </div>
              </div>
            ) : (
              <StepPreviewIframe
                routePattern={tourRoutePattern}
                targetId={targetMode === "anchor" ? targetId : ""}
                targetSelector={targetMode === "selector" ? targetSelector : ""}
              />
            )}
          </div>
        </div>
        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-step-cancel">
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={!canSubmit} data-testid="button-step-save">
            {mutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
